import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Cake, PartyPopper, CalendarClock, Gift } from 'lucide-react';
import { atlasApi, AtlasUser, Aniversariante, MarcoExperiencia } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

type Aba = 'aniversarios' | 'empresa' | 'experiencias';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const LembretesApp: React.FC<Props> = ({ user }) => {
  const [aba, setAba] = useState<Aba>('aniversarios');
  const [escopoNasc, setEscopoNasc] = useState<'hoje' | 'mes'>('mes');

  const [niver, setNiver] = useState<Aniversariante[]>([]);
  const [empresa, setEmpresa] = useState<Aniversariante[]>([]);
  const [exp, setExp] = useState<MarcoExperiencia[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    const tarefas: Promise<any>[] = [];
    if (aba === 'aniversarios') {
      tarefas.push(atlasApi.getAniversariantes(user, escopoNasc).then(r => setNiver(r.itens || [])));
    } else if (aba === 'empresa') {
      tarefas.push(atlasApi.getAniversariosEmpresa(user).then(r => setEmpresa(r.itens || [])));
    } else {
      tarefas.push(atlasApi.getExperiencias(user, 7).then(r => setExp(r.itens || [])));
    }
    Promise.all(tarefas)
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, aba, escopoNasc]);

  useEffect(() => { carregar(); }, [carregar]);

  const abas: { id: Aba; label: string; icon: React.ReactNode }[] = [
    { id: 'aniversarios', label: 'Aniversariantes', icon: <Cake className="w-4 h-4" /> },
    { id: 'empresa', label: 'Tempo de casa', icon: <PartyPopper className="w-4 h-4" /> },
    { id: 'experiencias', label: 'Experiência (30/60/90)', icon: <CalendarClock className="w-4 h-4" /> },
  ];

  const linhaPessoa = (
    key: React.Key, nome: string | undefined, cargo: string | undefined,
    funccodi: number, temFoto: boolean | undefined, direita: React.ReactNode,
  ) => (
    <div key={key} className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50/40 transition">
      <AvatarColaborador
        nome={nome}
        fotoUrl={temFoto ? atlasApi.fotoUrl(user, funccodi) : undefined}
        size={40}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{nome || '—'}</div>
        <div className="text-xs text-gray-400 truncate">{cargo || '—'}</div>
      </div>
      {direita}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {abas.map(a => (
          <button
            key={a.id}
            onClick={() => setAba(a.id)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition ${
              aba === a.id ? 'bg-purple-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {a.icon} {a.label}
          </button>
        ))}
      </div>

      {aba === 'aniversarios' && (
        <div className="flex gap-2">
          {(['hoje', 'mes'] as const).map(e => (
            <button
              key={e}
              onClick={() => setEscopoNasc(e)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                escopoNasc === e ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {e === 'hoje' ? 'Hoje' : 'Este mês'}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : erro ? (
          <div className="py-12 text-center text-rose-500 text-sm">{erro}</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {aba === 'aniversarios' && (niver.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">
                <Gift className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Nenhum aniversariante {escopoNasc === 'hoje' ? 'hoje' : 'neste mês'}.
              </div>
            ) : niver.map(p => linhaPessoa(
              p.funccodi, p.nome, p.cargo, p.funccodi, p.tem_foto,
              <div className="text-right">
                <div className="text-sm font-semibold text-purple-700">{p.dia} {MESES[p.mes - 1]}</div>
                {p.idade != null && <div className="text-xs text-gray-400">{p.idade} anos</div>}
              </div>,
            )))}

            {aba === 'empresa' && (empresa.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">Nenhum aniversário de empresa neste mês.</div>
            ) : empresa.map(p => linhaPessoa(
              p.funccodi, p.nome, p.cargo, p.funccodi, p.tem_foto,
              <div className="text-right">
                <div className="text-sm font-semibold text-emerald-700">{p.anos} {p.anos === 1 ? 'ano' : 'anos'}</div>
                <div className="text-xs text-gray-400">{p.dia} {MESES[p.mes - 1]}</div>
              </div>,
            )))}

            {aba === 'experiencias' && (exp.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">Nenhum marco de experiência nos próximos dias.</div>
            ) : exp.map(p => linhaPessoa(
              p.funccodi, p.nome, p.cargo, p.funccodi, p.tem_foto,
              <div className="text-right">
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                  {p.marco} dias
                </span>
                <div className="text-xs text-gray-400 mt-0.5">{p.dias_de_casa} dias de casa</div>
              </div>,
            )))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LembretesApp;
